/**
 * Alert Preferences Page — ProfitSpot
 * Pro/Unlimited users can customize their smart alert settings
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FeatureInfo } from '../components/FeatureInfo';
import { getBackendBaseUrl } from '../lib/apiBase';
import {
  Bell, Sliders, Save, RefreshCw, Crown, Lock,
  TrendingUp, Layers, Clock, Mail, Check, AlertTriangle, Send, Link, Unlink, X, Plus
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

const CHAINS = [
  { id: 'ethereum', name: 'Ethereum', color: '#627EEA' },
  { id: 'arbitrum', name: 'Arbitrum', color: '#28A0F0' },
  { id: 'polygon', name: 'Polygon', color: '#8247E5' },
  { id: 'optimism', name: 'Optimism', color: '#FF0420' },
  { id: 'base', name: 'Base', color: '#0052FF' },
  { id: 'bsc', name: 'BNB Chain', color: '#F0B90B' },
  { id: 'avalanche', name: 'Avalanche', color: '#E84142' },
];

const POPULAR_POOLS = [
  'ETH-USDC on Uniswap V3',
  'ETH-USDT on Uniswap V3',
  'stETH-ETH on Curve',
  'ARB-ETH on Camelot',
  'USDC-USDT on Curve',
  'WBTC-ETH on Uniswap V3',
  'OP-ETH on Velodrome',
  'SOL-USDC on Orca',
];

const POPULAR_PROTOCOLS = [
  'Uniswap', 'Aave', 'Curve', 'Compound', 'Lido',
  'MakerDAO', 'Balancer', 'SushiSwap', 'Convex', 'PancakeSwap',
  'GMX', 'Pendle', 'Yearn', 'Velodrome', 'Aerodrome',
];

const FREQUENCY_OPTIONS = [
  { id: 'realtime', label: 'Real-time', description: 'As soon as opportunities are found', hours: 1 },
  { id: 'hourly', label: 'Hourly Digest', description: 'Summary every hour', hours: 1 },
  { id: 'daily', label: 'Daily Digest', description: 'One summary per day', hours: 24 },
  { id: 'weekly', label: 'Weekly Digest', description: 'Weekly opportunity roundup', hours: 168 },
];

// ─── Pool Alerts Section (Feature 7) ─────────────────────────
const PoolAlertsSection = ({ token }) => {
  const [poolAlerts, setPoolAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const fetchAlerts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/alerts/pool-alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setPoolAlerts(data.alerts || []);
        }
      } catch (e) { console.warn('Pool alerts fetch failed:', e); }
      setLoading(false);
    };
    fetchAlerts();
  }, [token]);

  const handleToggle = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/alerts/${alertId}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPoolAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_active: data.is_active } : a));
        toast.success(data.is_active ? 'Alert enabled' : 'Alert paused');
      }
    } catch (e) { toast.error('Failed to toggle alert'); }
  };

  const handleDelete = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setPoolAlerts(prev => prev.filter(a => a.id !== alertId));
        toast.success('Alert deleted');
      }
    } catch (e) { toast.error('Failed to delete alert'); }
  };

  const alertTypeLabels = {
    apy_below: 'APY drops below',
    apy_above: 'APY rises above',
    tvl_drop: 'TVL drops by',
    rebalance: 'Rebalance needed',
  };

  if (loading) return <div className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Loading alerts...</div>;

  if (poolAlerts.length === 0) {
    return (
      <div className="text-center py-4">
        <Bell className="w-8 h-8 text-gray-700 mx-auto mb-2" />
        <p className="text-xs text-gray-500">No pool alerts set. Go to Yield Explorer and click the bell icon on any pool to create one.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {poolAlerts.map(alert => (
        <div key={alert.id} className={`flex items-center justify-between p-3 rounded-lg border ${alert.is_active ? 'bg-[#0a0a12] border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white truncate">{alert.pool_name}</span>
              {alert.triggered && <span className="text-[9px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded font-bold">TRIGGERED</span>}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {alertTypeLabels[alert.alert_type] || alert.alert_type}: {alert.threshold}%
              {alert.chain && <span className="ml-2">· {alert.chain}</span>}
            </div>
            {alert.last_triggered_at && (
              <div className="text-[10px] text-gray-600 mt-0.5">Last triggered: {new Date(alert.last_triggered_at).toLocaleDateString()}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <button onClick={() => handleToggle(alert.id)}
              className={`w-8 h-5 rounded-full transition-colors flex items-center ${alert.is_active ? 'bg-[#00ff88] justify-end' : 'bg-gray-700 justify-start'}`}>
              <div className="w-4 h-4 bg-white rounded-full shadow mx-0.5" />
            </button>
            <button onClick={() => handleDelete(alert.id)} className="text-gray-500 hover:text-red-400">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AlertPreferences() {
  const { user, token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState({
    min_apy: 20,
    max_il_risk: 'medium', // low, medium, high
    chains: ['ethereum', 'arbitrum', 'polygon', 'optimism', 'base'],
    protocols: [], // empty = all protocols
    pools: [],    // empty = all pools; specific = e.g. ["ETH-USDC on Uniswap V3"]
    stablecoins_only: false,
    frequency: 'daily',
    email_enabled: true,
    telegram_enabled: true,
  });

  const [poolInput, setPoolInput] = useState('');

  // Telegram state
  const [telegramStatus, setTelegramStatus] = useState({
    configured: false,
    linked: false,
    username: null,
  });
  const [linkCode, setLinkCode] = useState(null);
  const [loadingTelegram, setLoadingTelegram] = useState(false);

  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const isPremium = userTier === 'pro' || userTier === 'unlimited' || userTier === 'elite' || isAdmin;
  const isUnlimited = userTier === 'unlimited' || isAdmin;

  // Load existing preferences
  useEffect(() => {
    if (isAuthenticated && token) {
      loadPreferences();
      loadTelegramStatus();
    }
  }, [isAuthenticated, token]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
    setLoading(false);
  };

  const loadTelegramStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/alerts/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTelegramStatus(data);
      }
    } catch (err) {
      console.error('Failed to load Telegram status:', err);
    }
  };

  const getLinkCode = async () => {
    setLoadingTelegram(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/telegram/link-code`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLinkCode(data);
      }
    } catch (err) {
      toast.error('Failed to get link code');
    }
    setLoadingTelegram(false);
  };

  const unlinkTelegram = async () => {
    setLoadingTelegram(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/telegram/unlink`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setTelegramStatus({ configured: telegramStatus.configured, linked: false, username: null });
        setLinkCode(null);
        toast.success('Telegram unlinked');
      }
    } catch (err) {
      toast.error('Failed to unlink Telegram');
    }
    setLoadingTelegram(false);
  };

  const savePreferences = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/alerts/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(preferences),
      });

      if (res.ok) {
        setSaved(true);
        toast.success('Alert preferences saved!');
        setTimeout(() => setSaved(false), 3000);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save preferences');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    }
    setSaving(false);
  };

  const toggleChain = (chainId) => {
    setPreferences(prev => ({
      ...prev,
      chains: prev.chains.includes(chainId)
        ? prev.chains.filter(c => c !== chainId)
        : [...prev.chains, chainId],
    }));
  };

  const toggleProtocol = (protocol) => {
    setPreferences(prev => ({
      ...prev,
      protocols: (prev.protocols || []).includes(protocol)
        ? prev.protocols.filter(p => p !== protocol)
        : [...(prev.protocols || []), protocol],
    }));
  };

  const addPool = () => {
    const trimmed = poolInput.trim();
    if (!trimmed) return;
    if ((preferences.pools || []).includes(trimmed)) {
      setPoolInput('');
      return;
    }
    setPreferences(prev => ({ ...prev, pools: [...(prev.pools || []), trimmed] }));
    setPoolInput('');
  };

  const removePool = (pool) => {
    setPreferences(prev => ({ ...prev, pools: (prev.pools || []).filter(p => p !== pool) }));
  };


  // Auto-save notification toggle (lightweight — doesn't require full save)
  const toggleNotification = async (key) => {
    const newVal = !preferences[key];
    setPreferences(prev => ({ ...prev, [key]: newVal }));
    try {
      const res = await fetch(`${API_URL}/api/alerts/notification-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email_enabled: key === 'email_enabled' ? newVal : preferences.email_enabled,
          telegram_enabled: key === 'telegram_enabled' ? newVal : preferences.telegram_enabled,
        }),
      });
      if (res.ok) {
        toast.success(key === 'email_enabled' ? 'Email alerts updated' : 'Telegram alerts updated');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to update notification settings');
        // Revert on failure
        setPreferences(prev => ({ ...prev, [key]: !newVal }));
      }
    } catch (err) {
      toast.error('Network error — check your connection and try again.');
      setPreferences(prev => ({ ...prev, [key]: !newVal }));
    }
  };

  const togglePopularPool = (pool) => {
    setPreferences(prev => ({
      ...prev,
      pools: (prev.pools || []).includes(pool)
        ? prev.pools.filter(p => p !== pool)
        : [...(prev.pools || []), pool],
    }));
  };

  // Premium gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a12] flex items-center justify-center p-4">
        <div className="text-center">
          <Lock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Sign In Required</h2>
          <p className="text-gray-400 text-sm">Please sign in to access alert preferences.</p>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#0a0a12] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Crown className="w-12 h-12 text-[#ffa502] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Pro Feature</h2>
          <p className="text-gray-400 text-sm mb-4">
            Custom alert preferences are available for Pro and Unlimited subscribers.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00ff88] text-black font-bold rounded-lg hover:bg-[#00cc6a] transition-colors"
          >
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a12] p-4 md:p-6" data-testid="alert-preferences-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#ffa502]/10 rounded-lg">
              <Bell className="w-6 h-6 text-[#ffa502]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white">Alert Preferences</h1>
                <FeatureInfo featureKey="alerts" />
              </div>
              <p className="text-sm text-gray-400">Customize your Smart Yield Alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded font-bold ${
              isUnlimited ? 'bg-[#00ff88]/20 text-[#00ff88]' : 'bg-[#ffa502]/20 text-[#ffa502]'
            }`}>
              {userTier.toUpperCase()}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-[#00ff88] animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* APY Threshold */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#00ff88]" />
                <h2 className="text-base font-bold text-white">Minimum APY Threshold</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Only receive alerts for opportunities with APY above this threshold.
              </p>
              <div className="space-y-3">
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={preferences.min_apy}
                  onChange={(e) => setPreferences(prev => ({ ...prev, min_apy: Number(e.target.value) }))}
                  className="w-full accent-[#00ff88]"
                  data-testid="min-apy-slider"
                />
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">5%</span>
                  <span className="text-[#00ff88] font-bold text-lg">{preferences.min_apy}% APY</span>
                  <span className="text-gray-500">100%</span>
                </div>
              </div>
            </div>

            {/* IL Risk Tolerance */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#ffa502]" />
                <h2 className="text-base font-bold text-white">Impermanent Loss Risk Tolerance</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Maximum IL risk level you're comfortable with.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {['low', 'medium', 'high'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setPreferences(prev => ({ ...prev, max_il_risk: level }))}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      preferences.max_il_risk === level
                        ? level === 'low'
                          ? 'bg-green-500/20 border-green-500/50 text-green-400'
                          : level === 'medium'
                          ? 'bg-[#ffa502]/20 border-[#ffa502]/50 text-[#ffa502]'
                          : 'bg-red-500/20 border-red-500/50 text-red-400'
                        : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                    data-testid={`il-risk-${level}`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-3 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.stablecoins_only}
                  onChange={(e) => setPreferences(prev => ({ ...prev, stablecoins_only: e.target.checked }))}
                  className="w-4 h-4 accent-[#00ff88]"
                />
                <span className="text-sm text-gray-300">Stablecoin pools only (no IL risk)</span>
              </label>
            </div>

            {/* Chain Selection */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="w-5 h-5 text-[#00d4ff]" />
                <h2 className="text-base font-bold text-white">Chains to Monitor</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Select which blockchains to include in your alerts.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CHAINS.map((chain) => (
                  <button
                    key={chain.id}
                    onClick={() => toggleChain(chain.id)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                      preferences.chains.includes(chain.id)
                        ? 'bg-gray-800 border-[#00ff88]/50 text-white'
                        : 'bg-gray-900/50 border-gray-800 text-gray-500 hover:border-gray-700'
                    }`}
                    data-testid={`chain-${chain.id}`}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: chain.color }}
                    />
                    <span>{chain.name}</span>
                    {preferences.chains.includes(chain.id) && (
                      <Check className="w-4 h-4 text-[#00ff88] ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Protocol Filtering */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#ffa502]" />
                  <h2 className="text-base font-bold text-white">Protocols to Watch</h2>
                </div>
                {(preferences.protocols || []).length > 0 && (
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, protocols: [] }))}
                    className="text-xs text-gray-500 hover:text-white"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {(preferences.protocols || []).length === 0
                  ? 'Monitoring all protocols. Select specific ones to filter.'
                  : `Monitoring ${(preferences.protocols || []).length} protocol${preferences.protocols.length > 1 ? 's' : ''}.`}
              </p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_PROTOCOLS.map((protocol) => (
                  <button
                    key={protocol}
                    onClick={() => toggleProtocol(protocol)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      (preferences.protocols || []).includes(protocol)
                        ? 'bg-[#ffa502]/20 border-[#ffa502]/50 text-[#ffa502]'
                        : 'bg-gray-900/50 border-gray-800 text-gray-500 hover:border-gray-700 hover:text-gray-300'
                    }`}
                    data-testid={`protocol-${protocol.toLowerCase()}`}
                  >
                    {protocol}
                    {(preferences.protocols || []).includes(protocol) && (
                      <Check className="w-3 h-3 inline ml-1" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Pool-Specific Alerts */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#00d4ff]" />
                  <h2 className="text-base font-bold text-white">Specific Pools to Watch</h2>
                </div>
                {(preferences.pools || []).length > 0 && (
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, pools: [] }))}
                    className="text-xs text-gray-500 hover:text-white"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {(preferences.pools || []).length === 0
                  ? 'Watching all matching pools. Add specific pools to narrow down alerts.'
                  : `Watching ${(preferences.pools || []).length} specific pool${preferences.pools.length > 1 ? 's' : ''}.`}
              </p>

              {/* Popular pool quick-select */}
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Quick Add</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {POPULAR_POOLS.map((pool) => (
                  <button
                    key={pool}
                    onClick={() => togglePopularPool(pool)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                      (preferences.pools || []).includes(pool)
                        ? 'bg-[#00d4ff]/20 border-[#00d4ff]/50 text-[#00d4ff]'
                        : 'bg-gray-900/50 border-gray-800 text-gray-500 hover:border-gray-700 hover:text-gray-300'
                    }`}
                    data-testid={`pool-quick-${pool.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {pool}
                    {(preferences.pools || []).includes(pool) && (
                      <Check className="w-3 h-3 inline ml-1" />
                    )}
                  </button>
                ))}
              </div>

              {/* Custom pool input */}
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Custom Pool</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={poolInput}
                  onChange={(e) => setPoolInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addPool()}
                  placeholder="e.g. ETH-USDC on Uniswap V3"
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#00d4ff]/50"
                  data-testid="pool-custom-input"
                />
                <button
                  onClick={addPool}
                  disabled={!poolInput.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#00d4ff]/20 border border-[#00d4ff]/30 text-[#00d4ff] rounded-lg text-sm hover:bg-[#00d4ff]/30 transition-colors disabled:opacity-40"
                  data-testid="pool-add-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {/* Selected pools */}
              {(preferences.pools || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {preferences.pools.map((pool) => (
                    <span
                      key={pool}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00d4ff]/10 border border-[#00d4ff]/30 text-[#00d4ff] rounded-lg text-xs"
                      data-testid={`pool-chip-${pool.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {pool}
                      <button onClick={() => removePool(pool)} className="hover:text-white transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Alert Frequency */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-purple-400" />
                <h2 className="text-base font-bold text-white">Alert Frequency</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                How often would you like to receive alerts?
              </p>
              <div className="space-y-2">
                {FREQUENCY_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setPreferences(prev => ({ ...prev, frequency: option.id }))}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                      preferences.frequency === option.id
                        ? 'bg-purple-500/10 border-purple-500/50'
                        : 'bg-gray-900/50 border-gray-800 hover:border-gray-700'
                    }`}
                    data-testid={`frequency-${option.id}`}
                  >
                    <div>
                      <p className={`text-sm font-medium ${
                        preferences.frequency === option.id ? 'text-purple-400' : 'text-white'
                      }`}>
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                    {preferences.frequency === option.id && (
                      <Check className="w-5 h-5 text-purple-400" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Email Toggle */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <h2 className="text-base font-bold text-white">Email Alerts</h2>
                    <p className="text-xs text-gray-500">Receive alerts via email</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleNotification('email_enabled')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.email_enabled ? 'bg-[#00ff88]' : 'bg-gray-700'
                  }`}
                  data-testid="email-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    preferences.email_enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            {/* Telegram Integration */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Send className="w-5 h-5 text-[#0088cc]" />
                <h2 className="text-base font-bold text-white">Telegram Alerts</h2>
              </div>

              {!telegramStatus.configured ? (
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400">
                    Telegram integration is not configured yet. Contact your administrator to enable it.
                  </p>
                </div>
              ) : telegramStatus.linked ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0088cc]/20 rounded-full flex items-center justify-center">
                        <Send className="w-5 h-5 text-[#0088cc]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          @{telegramStatus.username || 'Connected'}
                        </p>
                        <p className="text-xs text-gray-500">Telegram linked</p>
                      </div>
                    </div>
                    <button
                      onClick={unlinkTelegram}
                      disabled={loadingTelegram}
                      className="flex items-center gap-2 px-3 py-2 text-xs text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors"
                      data-testid="unlink-telegram"
                    >
                      <Unlink className="w-3.5 h-3.5" />
                      Unlink
                    </button>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-800">
                    <span className="text-sm text-gray-400">Send alerts to Telegram</span>
                    <button
                      onClick={() => toggleNotification('telegram_enabled')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.telegram_enabled ? 'bg-[#0088cc]' : 'bg-gray-700'
                      }`}
                      data-testid="telegram-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        preferences.telegram_enabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400">
                    Link your Telegram to receive instant push notifications for yield alerts.
                  </p>
                  
                  {linkCode ? (
                    <div className="bg-[#0088cc]/10 border border-[#0088cc]/30 rounded-lg p-4">
                      <p className="text-xs text-gray-400 mb-2">Send this code to @ProfitSpotBot on Telegram:</p>
                      <div className="flex items-center justify-between bg-gray-900 rounded-lg p-3">
                        <code className="text-lg font-mono font-bold text-[#0088cc]">{linkCode.code}</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(linkCode.code);
                            toast.success('Code copied!');
                          }}
                          className="text-xs text-gray-400 hover:text-white"
                        >
                          Copy
                        </button>
                      </div>
                      <a
                        href={`https://t.me/${linkCode.bot_username || 'ProfitSpotBot'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full mt-3 py-2 bg-[#0088cc] text-white text-sm font-medium rounded-lg hover:bg-[#006699] transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        Open Telegram
                      </a>
                    </div>
                  ) : (
                    <button
                      onClick={getLinkCode}
                      disabled={loadingTelegram}
                      className="flex items-center justify-center gap-2 w-full py-3 bg-[#0088cc]/20 border border-[#0088cc]/30 text-[#0088cc] rounded-lg hover:bg-[#0088cc]/30 transition-colors disabled:opacity-50"
                      data-testid="link-telegram"
                    >
                      {loadingTelegram ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Link className="w-4 h-4" />
                      )}
                      Link Telegram Account
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Pool APY/TVL Alerts (Feature 7) */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-amber-400" />
                <h2 className="text-base font-bold text-white">Pool Alerts</h2>
                <FeatureInfo featureKey="pool_alerts" />
                <span className="text-[10px] text-gray-500">APY & TVL monitoring</span>
              </div>
              <PoolAlertsSection token={token} />
            </div>

            {/* Tier Info */}
            <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
              <p className="text-xs text-gray-400">
                <strong className="text-white">Your tier limits:</strong>{' '}
                {isUnlimited
                  ? 'Up to 3 persistent alerts (email + push) — fire when your conditions are met, auto-reset.'
                  : 'Up to 1 persistent alert (email + push) — stays live until it fires, then auto-resets.'}
                {!isUnlimited && (
                  <a href="/" className="text-[#00ff88] ml-1 hover:underline">
                    Upgrade to Unlimited for more alerts.
                  </a>
                )}
              </p>
            </div>

            {/* Save Button */}
            <button
              onClick={savePreferences}
              disabled={saving}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-base transition-all ${
                saved
                  ? 'bg-[#00ff88]/20 text-[#00ff88] border border-[#00ff88]/30'
                  : 'bg-[#00ff88] text-black hover:bg-[#00cc6a]'
              } disabled:opacity-50`}
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <Check className="w-5 h-5" />
                  Preferences Saved!
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
